import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { branchSchema, registerDeviceSchema, type BranchDTO, type BranchInput, type RegisterDeviceInput, type SeatQuoteDTO } from '@bifeai/shared';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  CardHeader,
  Dialog,
  EmptyState,
  Field,
  Input,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  PageHeader,
  Select,
  Skeleton,
  useToast,
} from '@bifeai/ui';
import { Archive, Building2, Ellipsis, MonitorSmartphone, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { api, applyFieldErrors, errorMessage } from '../lib/api';
import { formatDate, formatUsd, timeAgo } from '../lib/format';
import { useSession } from '../lib/session';
import { SwitchRow } from '../components/SwitchRow';

interface DeviceDTO {
  id: string;
  name: string;
  branchId: string;
  branchName: string | null;
  ticketSeries: number;
  lastSeenAt: string | null;
  createdAt: string;
}

export function BranchesPage() {
  const { me, can, isHeadquartersUser, franchisesEnabled, refresh } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState<BranchDTO | 'new' | null>(null);
  const [archiving, setArchiving] = useState<BranchDTO | null>(null);
  const [registering, setRegistering] = useState(false);

  const branches = useQuery({
    queryKey: ['branches'],
    queryFn: () => api.get<BranchDTO[]>('/branches'),
  });
  const devices = useQuery({
    queryKey: ['devices'],
    queryFn: () => api.get<DeviceDTO[]>('/devices'),
    enabled: can('branches.manage'),
  });

  const canManage = can('branches.manage');
  const multiBranch = me.tenant.modules.includes('MULTI_BRANCH');
  const canAdd = canManage && isHeadquartersUser && (multiBranch || (branches.data?.length ?? 0) === 0);

  const archive = useMutation({
    mutationFn: (id: string) => api.del(`/branches/${id}`),
    onSuccess: async () => {
      toast.success('Sucursal archivada');
      setArchiving(null);
      await Promise.all([qc.invalidateQueries({ queryKey: ['branches'] }), refresh()]);
    },
    onError: (err) => toast.error('No se pudo archivar', errorMessage(err)),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.del(`/devices/${id}`),
    onSuccess: async () => {
      toast.success('Terminal dada de baja');
      await qc.invalidateQueries({ queryKey: ['devices'] });
    },
    onError: (err) => toast.error('No se pudo dar de baja', errorMessage(err)),
  });

  return (
    <>
      <PageHeader
        title="Sucursales"
        subtitle={
          isHeadquartersUser
            ? 'La casa central ve todas las sucursales. Cada sucursal ve solo sus propios datos.'
            : 'Estos son los datos de tu sucursal.'
        }
        actions={
          canAdd && (
            <Button variant="primary" icon={<Plus />} onClick={() => setEditing('new')}>
              Nueva sucursal
            </Button>
          )
        }
      />

      {canManage && isHeadquartersUser && !multiBranch && (
        <Callout>
          Tu plan incluye una sucursal. Para sumar más locales y verlos juntos desde la casa central, pedí el módulo <strong>Multisucursal</strong>.
        </Callout>
      )}

      <Card>
        <CardBody flush>
          {branches.isPending ? (
            <div style={{ padding: '8px 22px 16px', display: 'grid', gap: 12 }}>
              <Skeleton />
              <Skeleton />
            </div>
          ) : branches.data?.length === 0 ? (
            <EmptyState icon={<Building2 />} title="Todavía no hay sucursales" text="Cargá tu primera sucursal para empezar." />
          ) : (
            <div className="bf-table-wrap">
              <table className="bf-table">
                <thead>
                  <tr>
                    <th>Sucursal</th>
                    <th>Dirección</th>
                    <th>Teléfono</th>
                    <th>Alta</th>
                    {canManage && <th aria-label="Acciones" />}
                  </tr>
                </thead>
                <tbody>
                  {branches.data?.map((b) => (
                    <tr key={b.id}>
                      <td>
                        <span className="bf-cell-main">{b.name}</span> {b.isHeadquarters && <Badge tone="info">Casa central</Badge>}
                        {b.franchise && <Badge tone="warning">Franquicia</Badge>}
                      </td>
                      <td>{b.address ?? <span className="bf-cell-sub">Sin cargar</span>}</td>
                      <td>{b.phone ?? <span className="bf-cell-sub">Sin cargar</span>}</td>
                      <td className="bf-cell-sub">{formatDate(b.createdAt)}</td>
                      {canManage && (
                        <td className="bf-cell-actions">
                          <Menu>
                            <MenuTrigger asChild>
                              <button className="bf-icon-btn" aria-label={`Acciones para ${b.name}`}>
                                <Ellipsis />
                              </button>
                            </MenuTrigger>
                            <MenuContent>
                              <MenuItem icon={<Pencil />} onSelect={() => setEditing(b)}>
                                Editar datos
                              </MenuItem>
                              {!b.isHeadquarters && isHeadquartersUser && (
                                <MenuItem icon={<Archive />} danger onSelect={() => setArchiving(b)}>
                                  Archivar sucursal
                                </MenuItem>
                              )}
                            </MenuContent>
                          </Menu>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {canManage && (
        <Card>
          <CardHeader
            title="Terminales de mostrador"
            subtitle="Computadoras o tablets donde los cajeros entran con su PIN. Cada una tiene su propia numeración de tickets."
            actions={
              <Button icon={<MonitorSmartphone />} onClick={() => setRegistering(true)}>
                Registrar esta computadora
              </Button>
            }
          />
          <CardBody flush>
            {devices.data?.length === 0 ? (
              <EmptyState
                icon={<MonitorSmartphone />}
                title="Sin terminales registradas"
                text="Abrí BifeAI en la computadora del mostrador y registrala desde acá."
              />
            ) : (
              <div className="bf-table-wrap">
                <table className="bf-table">
                  <thead>
                    <tr>
                      <th>Terminal</th>
                      <th>Sucursal</th>
                      <th>Serie de tickets</th>
                      <th>Último uso</th>
                      <th aria-label="Acciones" />
                    </tr>
                  </thead>
                  <tbody>
                    {devices.data?.map((d) => (
                      <tr key={d.id}>
                        <td className="bf-cell-main">{d.name}</td>
                        <td>{d.branchName}</td>
                        <td className="bf-num">{String(d.ticketSeries).padStart(4, '0')}</td>
                        <td className="bf-cell-sub">{d.lastSeenAt ? timeAgo(d.lastSeenAt) : 'Sin uso todavía'}</td>
                        <td className="bf-cell-actions">
                          <Button
                            size="sm"
                            variant="ghost"
                            icon={<Trash2 />}
                            onClick={() => revoke.mutate(d.id)}
                            loading={revoke.isPending && revoke.variables === d.id}
                          >
                            Dar de baja
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {editing && (
        <BranchDialog
          branch={editing === 'new' ? null : editing}
          canSetFranchise={
            franchisesEnabled && isHeadquartersUser && multiBranch && (editing === 'new' ? (branches.data?.length ?? 0) > 0 : !editing.isHeadquarters)
          }
          onClose={() => setEditing(null)}
          onSaved={async (name, isNew) => {
            toast.success(isNew ? 'Sucursal creada' : 'Sucursal actualizada', name);
            setEditing(null);
            await Promise.all([
              qc.invalidateQueries({ queryKey: ['branches'] }),
              qc.invalidateQueries({ queryKey: ['seat-quote'] }),
              qc.invalidateQueries({ queryKey: ['my-plan'] }),
              refresh(),
            ]);
          }}
        />
      )}

      <Dialog
        open={Boolean(archiving)}
        onOpenChange={(o) => !o && setArchiving(null)}
        title="Archivar sucursal"
        description={`${archiving?.name ?? ''} deja de aparecer en la app. Sus datos se conservan para estadísticas y auditoría.`}
      >
        <div className="bf-form-actions">
          <Button onClick={() => setArchiving(null)}>Cancelar</Button>
          <Button variant="danger" icon={<Archive />} loading={archive.isPending} onClick={() => archiving && archive.mutate(archiving.id)}>
            Archivar
          </Button>
        </div>
      </Dialog>

      {registering && (
        <RegisterDeviceDialog
          branches={branches.data ?? []}
          onClose={() => setRegistering(false)}
          onDone={async () => {
            toast.success('Terminal registrada', 'Desde esta computadora los cajeros ya pueden entrar con su PIN.');
            setRegistering(false);
            await qc.invalidateQueries({ queryKey: ['devices'] });
          }}
        />
      )}
    </>
  );
}

function BranchDialog({
  branch,
  canSetFranchise,
  onClose,
  onSaved,
}: {
  branch: BranchDTO | null;
  canSetFranchise: boolean;
  onClose: () => void;
  onSaved: (name: string, isNew: boolean) => Promise<void>;
}) {
  const [formError, setFormError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    setError,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<BranchInput>({
    resolver: zodResolver(branchSchema),
    defaultValues: {
      name: branch?.name ?? '',
      address: branch?.address ?? '',
      phone: branch?.phone ?? '',
      franchise: branch?.franchise ?? false,
    },
  });
  const franchise = watch('franchise') ?? false;
  const changed = !!branch && franchise !== branch.franchise;
  // Lo que cuesta sumar este local o franquicia (si no entra en lo que ya paga): se muestra antes de confirmar.
  const adds = !branch || changed;
  const quote = useQuery({
    queryKey: ['seat-quote', franchise],
    queryFn: () => api.get<SeatQuoteDTO>(`/billing/seat-quote${franchise ? '?franquicia=1' : ''}`),
    enabled: adds,
    staleTime: 30_000,
  });
  const charge = adds && quote.data?.required ? quote.data : null;
  const franchiseePays = adds && franchise && quote.data?.payer === 'FRANCHISEE';

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      const body = { ...values, confirmCharge: !!charge };
      const saved = branch ? await api.patch<BranchDTO>(`/branches/${branch.id}`, body) : await api.post<BranchDTO>('/branches', body);
      await onSaved(saved.name, !branch);
    } catch (err) {
      if (!applyFieldErrors(err, setError)) setFormError(errorMessage(err));
    }
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={branch ? 'Editar sucursal' : 'Nueva sucursal'}>
      <form className="bf-form" onSubmit={onSubmit} noValidate>
        {formError && <Callout tone="danger">{formError}</Callout>}
        <Field label="Nombre" error={errors.name?.message}>
          <Input placeholder="Sucursal Norte" autoFocus {...register('name')} />
        </Field>
        <Field label="Dirección" error={errors.address?.message}>
          <Input placeholder="Av. Cabildo 2500, CABA" {...register('address')} />
        </Field>
        <Field label="Teléfono" error={errors.phone?.message}>
          <Input placeholder="11 5555-0000" {...register('phone')} />
        </Field>
        {canSetFranchise && (
          <div className="bf-form" style={{ gap: 6 }}>
            <SwitchRow label="Es una franquicia" checked={franchise} onChange={(v) => setValue('franchise', v, { shouldDirty: true })} />
            <p className="bf-hint" style={{ margin: 0 }}>
              Una franquicia es independiente con su plata: conecta <strong>su propio Mercado Pago</strong> y factura con{' '}
              <strong>su CUIT y su certificado de ARCA</strong>. Lo que cobra va a su cuenta, no a la de la carnicería. La casa central igual ve todo
              lo que vende y factura. Si es un local tuyo, dejalo apagado: cobra y factura con tus cuentas.
            </p>
            {changed && (
              <Callout tone="warning">
                Al guardar, esta sucursal deja de usar la caja y los posnets de Mercado Pago y el punto de venta de ARCA que tenía: hay que
                configurarlos de nuevo con las cuentas {franchise ? 'de la franquicia' : 'de la carnicería'} (Configuración → Pagos y Facturación).
              </Callout>
            )}
          </div>
        )}
        {charge && (
          <Callout tone="info">
            {franchise ? 'Esta franquicia' : 'Este local'} suma{' '}
            <strong>
              {formatUsd(charge.yearlyCents)} por {charge.monthly ? 'mes' : 'año'}
            </strong>{' '}
            a tu {charge.monthly ? 'abono' : 'mantenimiento'}. Hoy se cobra <strong>{formatUsd(charge.amountCents)}</strong> con tu tarjeta: lo
            proporcional hasta tu {charge.monthly ? 'próximo cobro' : 'renovación'}
            {charge.renewal ? ` del ${formatDate(charge.renewal)}` : ''}, así después pagás todo junto.
          </Callout>
        )}
        {franchiseePays && quote.data && (
          <Callout tone="info">
            {quote.data.monthly ? 'El abono' : 'La licencia'} de esta franquicia la paga el franquiciado:{' '}
            <strong>
              {formatUsd(quote.data.yearlyCents)} por {quote.data.monthly ? 'mes' : 'año'}
            </strong>
            , desde su usuario (Mi plan). A vos no se te cobra nada.
          </Callout>
        )}
        <div className="bf-form-actions">
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="primary" loading={isSubmitting} disabled={adds && quote.isLoading}>
            {charge ? `${branch ? 'Guardar' : 'Crear'} y pagar ${formatUsd(charge.amountCents)}` : branch ? 'Guardar cambios' : 'Crear sucursal'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function RegisterDeviceDialog({ branches, onClose, onDone }: { branches: BranchDTO[]; onClose: () => void; onDone: () => Promise<void> }) {
  const [formError, setFormError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<RegisterDeviceInput>({
    resolver: zodResolver(registerDeviceSchema),
    defaultValues: { name: 'Caja 1', branchId: branches[0]?.id ?? '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await api.post('/devices', values);
      await onDone();
    } catch (err) {
      if (!applyFieldErrors(err, setError)) setFormError(errorMessage(err));
    }
  });

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title="Registrar esta computadora"
      description="Hacelo desde la computadora o tablet del mostrador. Queda vinculada a la sucursal que elijas."
    >
      <form className="bf-form" onSubmit={onSubmit} noValidate>
        {formError && <Callout tone="danger">{formError}</Callout>}
        <Field label="Nombre de la terminal" error={errors.name?.message}>
          <Input autoFocus {...register('name')} />
        </Field>
        <Field label="Sucursal" error={errors.branchId?.message}>
          <Select {...register('branchId')}>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </Select>
        </Field>
        <div className="bf-form-actions">
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="primary" loading={isSubmitting}>
            Registrar
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
